// A small per-row / per-card overflow menu (Session AAX, Loop 4: one action grammar).
// Secondary and destructive actions live here so each section keeps exactly one visible
// primary. The trigger is a quiet bordered glyph; the menu closes on outside click or
// Escape, and clicks INSIDE it never bubble to a clickable row (two-step ConfirmButton
// items need their second click to land in the still-open menu).
//
// Session AFO: the panel is PORTALED to document.body and positioned `fixed` from the
// trigger, instead of rendering inline as `absolute right-0`. An inline absolute panel is
// clipped by any ancestor with overflow-hidden, which is exactly the office list-table
// wrapper -- Session AEW fixed CalloutsPage page-locally (stripping its wrapper's
// overflow-hidden) and flagged ProjectsPage (and the since-deleted ProspectsPage) as carrying the identical
// clip around this identical menu. Portaling out of the clipping ancestor fixes every
// consumer (and any future one) at once, so a bottom-row Cancel / Delete is always clickable.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";

// WHERE THE PANEL GOES (Session FR4, the layout gate's popup-offscreen finding: the Call-outs menu was off the left edge at 360 and 412
// wide under font scale 1.3 and 1.5). The old placement anchored the panel by its RIGHT edge to the trigger's right edge and never
// looked at the panel's width, so a trigger sitting nearer the left than the panel is wide pushed the panel past the left edge, and
// large text makes the panel wider. One pure function now decides it from three rectangles: the panel is right-aligned to the trigger
// when it fits, otherwise slid back inside the screen (4px margin), capped to the screen's width, and when it fits neither below
// nor above the trigger it takes the side with more room and scrolls inside its own box. A panel whose size is not known yet
// (its first, unmeasured render) keeps the old right-anchored placement for that one frame; the layout effect re-runs this once it is.
export type MenuRect = { left: number; right: number; top: number; bottom: number };
export function placeMenu(
  trigger: MenuRect,
  panel: { w: number; h: number },
  viewport: { w: number; h: number },
  gap = 4,
  margin = 4,
): CSSProperties {
  const below = trigger.bottom + gap;
  const maxWidth = Math.max(0, viewport.w - 2 * margin);
  // The panel's class gives it min-w-48 (12rem), which is in rem: under large text it grows past the screen, and a CSS min-width beats a
  // max-width, so the cap alone did nothing (FR4 review M2). The minimum is therefore never more than the room there is.
  const minWidth = `min(12rem, ${maxWidth}px)`;
  if (panel.w <= 0 || panel.h <= 0) {
    return { position: "fixed", right: Math.max(margin, viewport.w - trigger.right), top: below, maxWidth, minWidth };
  }
  const w = Math.min(panel.w, maxWidth);
  const left = Math.max(margin, Math.min(trigger.right - w, viewport.w - margin - w));
  const roomBelow = viewport.h - below - margin;
  const roomAbove = trigger.top - gap - margin;
  const flipUp = panel.h > roomBelow && roomAbove > roomBelow;
  const room = flipUp ? roomAbove : roomBelow;
  const style: CSSProperties = { position: "fixed", left, maxWidth, minWidth };
  if (flipUp) style.bottom = viewport.h - trigger.top + gap;
  else style.top = below;
  if (panel.h > room) {
    style.maxHeight = Math.max(room, 96);
    style.overflowY = "auto";
  }
  return style;
}

// closeOnSelect (Session SC7B): close the menu when an item with role="menuitem" is pressed. Off by default, because a
// two-step ConfirmButton item needs its second press to land in the still-open menu; on for a menu whose items open a modal
// or go somewhere, which should not be left hanging open behind what they opened.
// triggerContent / triggerClassName (Session DOC1): a caller whose trigger is not the quiet bordered "more" glyph (the office header's
// Documents icon) supplies its own content and classes. Both default to the glyph and its classes, so every other menu is byte
// for byte as before.
export function OverflowMenu({
  label = "More actions",
  children,
  closeOnSelect = false,
  triggerContent,
  triggerClassName,
}: {
  label?: string;
  children: ReactNode;
  closeOnSelect?: boolean;
  triggerContent?: ReactNode;
  triggerClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<CSSProperties | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null); // the wrapper holding the trigger button
  const panelRef = useRef<HTMLDivElement>(null);

  // Position the portaled panel under (or, when there is no room below, above) the trigger,
  // right-aligned like the old `right-0 mt-1`. Read the live trigger rect each time so the menu
  // stays pinned while the page scrolls or resizes.
  const reposition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;
    const r = trigger.getBoundingClientRect();
    const panel = panelRef.current;
    // scrollHeight is the panel's natural height even after placeMenu has capped it, so a later reposition does not read the cap. (The
    // items do not shrink inside a capped box: see the panel's [&>*]:shrink-0, or a capped box would read shorter than it is and the
    // cap would come off and go on again at every scroll.) The width is the document's own (clientWidth), which leaves out a classic
    // scrollbar that innerWidth includes; innerWidth is the fallback for a document with no root element size.
    setCoords(
      placeMenu(
        { left: r.left, right: r.right, top: r.top, bottom: r.bottom },
        { w: panel?.offsetWidth ?? 0, h: panel ? Math.max(panel.offsetHeight, panel.scrollHeight) : 0 },
        { w: document.documentElement.clientWidth || window.innerWidth, h: window.innerHeight },
      ),
    );
  }, []);

  // After the panel mounts its height is known, so re-run the flip decision, and keep it pinned to
  // the trigger while open. Cleaned up on close.
  useLayoutEffect(() => {
    if (!open) return;
    reposition();
    // Session AFO review fix: the portal appends the panel to the end of <body>, so without moving
    // focus into it a keyboard user tabbing from the trigger would skip past every menu item (they
    // are now last in the document, not the trigger's next DOM sibling). Focus the first item on open
    // -- the WAI-ARIA menu-button pattern, and what ModalShell does with panel.focus().
    panelRef.current?.querySelector<HTMLElement>('button, a[href], [tabindex]:not([tabindex="-1"])')?.focus();
    const onScroll = () => reposition();
    window.addEventListener("scroll", onScroll, true); // capture: catch scrolls in any container
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, reposition]);

  // Close on an outside click or Escape. The panel is no longer a DOM descendant of the trigger, so
  // the outside test must check BOTH refs, otherwise every in-menu click would read as "outside"
  // and close the menu -- breaking the two-step ConfirmButton grammar.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!triggerRef.current?.contains(t) && !panelRef.current?.contains(t)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        // Return focus to the trigger on a keyboard close (an outside CLICK leaves focus where the
        // user clicked), so focus is never stranded in a menu that no longer exists.
        triggerRef.current?.querySelector<HTMLElement>("button")?.focus();
      }
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={triggerRef} className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        // Compute a first position from the trigger BEFORE the panel renders, so it never flashes at
        // the top-left; the useLayoutEffect then refines it once the panel height is measurable.
        onClick={() => {
          if (!open) reposition();
          setOpen((o) => !o);
        }}
        // max-sm: the ~26px trigger is well under the 44px tap-target floor on phones; bump it
        // there only (desktop stays byte-identical).
        className={
          triggerClassName ??
          "rounded-ele border border-ele-purple-accent px-2.5 py-1.5 font-menu text-xs leading-none text-ele-body transition hover:text-ele-strong max-sm:inline-flex max-sm:min-h-11 max-sm:min-w-11 max-sm:items-center max-sm:justify-center"
        }
      >
        {triggerContent ?? <>&#8943;</>}
      </button>
      {open
        ? createPortal(
            // The panel re-applies the .office-ui scope (like ModalShell): document.body sits OUTSIDE
            // the OfficeLayout wrapper that carries the office design tokens, so without it a portaled
            // menu would lose its themed colours. onClick stopPropagation stops a click bubbling
            // THROUGH THE REACT TREE to the row underneath -- React portals bubble by React tree, not
            // DOM tree -- which the two-step ConfirmButton grammar depends on.
            <div
              ref={panelRef}
              role="menu"
              aria-label={label}
              onClick={(e) => {
                e.stopPropagation();
                if (closeOnSelect && (e.target as HTMLElement).closest('[role="menuitem"]')) {
                  // Focus goes back to the trigger BEFORE the menu unmounts, so it is never left inside a menu that no longer exists
                  // (SC7B review). The item's own handler has already run: if it opened a modal, that modal takes focus when it mounts
                  // (after this), so the modal's own focus management wins.
                  triggerRef.current?.querySelector<HTMLElement>("button")?.focus();
                  setOpen(false);
                }
              }}
              style={coords ?? { position: "fixed" }}
              className="office-ui ele-pop-in z-50 flex min-w-48 flex-col gap-1 [&>*]:shrink-0 rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] p-2 text-ele-body shadow-lg"
            >
              {children}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

// The standard quiet menu-item styling for OverflowMenu children (buttons or ConfirmButtons).
// max-md: the ~28px row is under the 44px tap-target floor on phones; bump it there only. It is max-md (below 768px, the same line as
// the phone floor in index.css), NOT max-sm (below 640px): the panel is portaled to <body> with .office-ui on the panel itself, so that
// floor's `.office-ui .flex > a` can never reach a LINK row (a button row is reached by `.office-ui button`), and a phone held sideways
// is 640 to 767px wide. With max-sm a link row in a menu that shows at those widths was 30px tall beside 44px button rows (Session DOC1-TIDY).
export const overflowItemCls =
  "w-full rounded-ele border border-transparent px-3 py-1.5 text-left font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong max-md:min-h-11 max-md:flex max-md:items-center";
export const overflowDangerItemCls =
  "w-full rounded-ele border border-transparent px-3 py-1.5 text-left font-menu text-xs tracking-wide text-ele-danger transition hover:bg-red-500/10 max-md:min-h-11 max-md:flex max-md:items-center";
